import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Navbar from "./components/navbar.js";
import Home from "./pages/home.js";
import News from "./pages/news.js";
import "./index.css";
import About from './pages/about.js';
import Footer from './components/footer.js';
import AdminQuickBar from './components/AdminQuickBar.js';
import Trainings from "./pages/training.js";
import Instructors from "./pages/instructors.js";
import GeneralDetails from './pages/registeration/generalDetails.js';
import CourseSelection from './pages/registeration/courseSelection.js';
import Submit from './pages/registeration/submit.js';
import RegistrationList from './pages/xplore-form.js';
import AdminDashboard from './pages/adminDashboard.js';
import AdminLogin from './pages/adminLogin.js';
import NotFound from './pages/notFound.js';
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './lib/firebase';

import { verifyOrInitAdmin } from './lib/firestoreContent';

const ROUTE_TITLES = {
  '/': 'XploreSTEMi — Empowering African Youth in STEM',
  '/training': 'Training & Programs | XploreSTEMi',
  '/instructors': 'Meet the Instructors | XploreSTEMi',
  '/about': 'About Our Mission & Vision | XploreSTEMi',
  '/news': 'Latest News & Updates | XploreSTEMi',
  '/register': 'Register for Bootcamp | XploreSTEMi',
  '/course-selection': 'Course Selection | XploreSTEMi',
  '/submit': 'Review & Submit Application | XploreSTEMi',
  '/admin': 'Admin Dashboard | XploreSTEMi',
  '/form': 'Registration Records | XploreSTEMi',
};

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [adminRole, setAdminRole] = useState("Admin");
  const [authChecked, setAuthChecked] = useState(false);

  // Sync document title with current route
  useEffect(() => {
    document.title = ROUTE_TITLES[location.pathname] || 'Page Not Found | XploreSTEMi';
  }, [location.pathname]);

  // Global keyboard shortcut: Ctrl+Shift+A or Cmd+Shift+A -> /admin
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        navigate('/admin');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const verification = await verifyOrInitAdmin(user);
          if (verification.isAuthorized) {
            setAdminUser(user);
            setAdminRole(verification.role || "Admin");
            setIsAdminAuthenticated(true);
          } else {
            console.warn("Unauthorized user logged in. Signing out:", user.email);
            await signOut(auth);
            setIsAdminAuthenticated(false);
            setAdminUser(null);
          }
        } catch (e) {
          console.error("Admin verification error:", e);
          if (user.email && user.email.toLowerCase().trim() === "ukanet4christ@gmail.com") {
            setAdminUser(user);
            setAdminRole("Super Admin");
            setIsAdminAuthenticated(true);
          } else {
            setIsAdminAuthenticated(false);
            setAdminUser(null);
          }
        }
      } else {
        setIsAdminAuthenticated(false);
        setAdminUser(null);
      }
      setAuthChecked(true);
    });
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  const handleLogin = (user, role) => {
    setAdminUser(user);
    if (role) setAdminRole(role);
    setIsAdminAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
    setAdminUser(null);
    setIsAdminAuthenticated(false);
  };

  return (
    <div>
      {!isAdminRoute && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/training" element={<Trainings />} />
        <Route path="/instructors" element={<Instructors />} />
        <Route path="/about" element={<About />} />
        <Route path="/news" element={<News />} />
        <Route path="/register" element={<GeneralDetails />} />
        <Route path="/course-selection" element={<CourseSelection />} />
        <Route path="/submit" element={<Submit />} />
        <Route
          path="/form"
          element={
            isAdminAuthenticated ? (
              <RegistrationList />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />
        <Route
          path="/admin"
          element={
            !authChecked ? (
              <div className="flex min-h-screen items-center justify-center">
                <p className="text-slate-500">Loading…</p>
              </div>
            ) : isAdminAuthenticated ? (
              <AdminDashboard currentUser={adminUser} userRole={adminRole} onLogout={handleLogout} />
            ) : (
              <AdminLogin onLogin={handleLogin} />
            )
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isAdminRoute && (
        <>
          <Footer />
          {isAdminAuthenticated && (
            <AdminQuickBar
              adminUser={adminUser}
              adminRole={adminRole}
              onLogout={handleLogout}
            />
          )}
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

export default App;
